"use client";

import { Icon, ospThemes } from "@ospweb/sdk-ui";
import React, { useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "next/navigation";
import { cn } from "utils";
import log from "logbeacon";
import { useQueryClient } from "@tanstack/react-query";
import { CommonConfirmModalCallable } from "../../modal/CommonConfirmModal";
import { EdgenModalCreateAble } from "../../EdgenModal";
import { MainHeader } from "../../mainHeader";
import { Transaction, TransactionType } from "./types";

import { TransactionCreateAble } from "./TransactionModal/TransactionModal";
import { TransactionTable } from "./TransactionTable";
import { I18nUtil } from "@utils/i18n";
import { PositionAssetTypeEnum } from "@/src/features/api/memberApi";
import { QUERY_KEYS } from "@/src/config";
import Translations from "@translate/components/Portfolio/Transactions/TransactionsDetails";
import { calculateTotal } from "@/src/utils/decimalMath";
import { sleep } from "@/src/utils/sleep";
import { useDeletePositionOrder } from "@/src/hooks/usePositionOrder";
import { useIsPc, useShellRouter } from "@/src/hooks";
import { usePositionOrders } from "@/src/hooks/usePositionOrders";
import { usePortfolioType } from "@/src/hooks/usePortfolioType";
import { PortfolioType } from "@/src/components/Portfolio/types";

const I18n = I18nUtil.createScoped(Translations);

/**
 * Convert PositionOrder to Transaction format
 */
const positionOrderToTransaction = (order: any): Transaction => {
  const isBuy = order.side === "BUY";
  log.debug("[positionOrderToTransaction]:", order);
  return {
    id: order.order_id,
    platform_order_id: order.platform_order_id,
    type: isBuy ? TransactionType.Buy : TransactionType.Sell,
    coin: order.asset?.symbol || "Unknown",
    coinIcon: order.asset?.logo || "",
    quantity: Math.abs(order.amount) || 0,
    pricePerCoin: order.price || 0,
    dateTime: new Date(Number(order.order_time)).toLocaleString(),
    fees: order.fee || 0,
    cost: calculateTotal(
      order.amount,
      order.price,
      order.fee,
      isBuy ? "BUY" : "SELL",
    ),
    assetType: order.asset_type,
    orderId: order.order_id,
  };
};

interface TransactionsDetailsContentProps {
  portfolioId: string;
  assetId: string;
  assetType: PositionAssetTypeEnum;
  onClose?: () => void;
  isModal?: boolean;
  symbol?: string;
  currency?: string;
}

/**
 * TransactionsDetailsContent - Content component that can be used in both page and modal
 */
const TransactionsDetailsContent: React.FC<TransactionsDetailsContentProps> = ({
  portfolioId: id,
  assetId,
  assetType,
  onClose,
  isModal = false,
  symbol: propSymbol,
  currency = "USD",
}) => {
  const router = useShellRouter();
  const queryClient = useQueryClient();
  const [isPc] = useIsPc();
  const searchParams = useSearchParams();
  const { currentType } = usePortfolioType();

  // Get symbol from props (for modal) or URL searchParams (for page navigation)
  const symbol = useMemo(() => {
    if (propSymbol) {
      return propSymbol;
    }
    // For page mode, read from URL searchParams
    return searchParams.get("symbol") || undefined;
  }, [propSymbol, searchParams]);

  // Get position orders using hook
  const {
    data: positionOrdersData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePositionOrders(id, {
    asset_id: assetId,
    asset_type: assetType,
  });

  // Convert position orders to transactions
  const transactions = useMemo(() => {
    if (!positionOrdersData?.orders) {
      return [];
    }
    return positionOrdersData.orders.map(positionOrderToTransaction);
  }, [positionOrdersData]);

  // Hook for deleting orders
  const deletePositionOrder = useDeletePositionOrder({
    onSuccess: async () => {
      await sleep(2500);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POSITION_ORDERS] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PORTFOLIO_ASSETS_INFINITE, id],
      });
    },
  });

  const handleAddTrade = useCallback(async () => {
    await TransactionCreateAble({
      mode: "add",
      disabledSelect: true,
      initialData: {
        assetId,
        assetType,
        currency,
        accountId: id,
      },
    });
  }, [assetId, id, assetType]);

  const handleEdit = useCallback(
    async (transaction: Transaction) => {
      await TransactionCreateAble({
        mode: "edit",
        disabledSelect: true,
        initialData: {
          assetId,
          assetType: transaction.assetType || PositionAssetTypeEnum.TOKEN,
          accountId: id,
          orderId: transaction.orderId,
          quantity: transaction.quantity,
          type: transaction.type,
          dateTime: transaction.dateTime,
          fees: transaction.fees,
          pricePerCoin: transaction.pricePerCoin,
          platform_order_id: transaction.platform_order_id,
          currency,
        },
      });
    },
    [id, assetId],
  );

  const handleDelete = useCallback(
    async (transaction: Transaction) => {
      await CommonConfirmModalCallable({
        title: I18n.t("Delete Transaction"),
        description: I18n.t(
          "Are you sure you want to delete this transaction?",
        ),
        onConfirm: () => {
          deletePositionOrder.mutate({
            accountId: id || "",
            orderId: transaction.orderId || "",
          });
        },
      });
    },
    [id, deletePositionOrder],
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleGoBack = useCallback(() => {
    if (isModal && onClose) {
      onClose();
    } else {
      router.back();
    }
  }, [isModal, onClose, router]);

  return (
    <div
      className={cn(
        "bg-background_solid_secondary",
        isModal ? "h-full flex flex-col" : "pb-12",
      )}
    >
      {!isModal && (
        <MainHeader className="h-13 md:w-auto z-20" goBack={router.back} />
      )}

      {/* H5 Modal Header */}
      {isModal && !isPc && (
        <div className="flex gap-6 items-center justify-center p-4 shrink-0 h-15 sticky">
          <div className="flex gap-2 items-center grow min-w-0">
            <button
              data-openreplay-label="Go back Button"
              id="btn-go-back"
              onClick={handleGoBack}
              className="shrink-0 flex items-center justify-center"
              aria-label="Go back"
            >
              <Icon
                name="left-arrow-alt"
                size={24}
                className="text-content_primary"
              />
            </button>
            <h1
              aria-label="Transactions Details h1"
              data-openreplay-label="Transactions Details h1 Semantic"
              id="sem-transactions-details-h1"
              className="headline_regular_s text-content_primary truncate"
            >
              {symbol} {I18n.t("Transactions Details")}
            </h1>
          </div>
          {onClose && (
            <button
              data-openreplay-label="Close Button"
              id="btn-close-transactions-transactionsdetails-5"
              onClick={onClose}
              className="shrink-0 flex items-center justify-center"
              aria-label="Close"
            >
              <Icon name="close" size={20} className="text-content_tertiary" />
            </button>
          )}
        </div>
      )}

      {/* H5 Modal: TransactionTable with scrollable area */}
      {isModal && !isPc ? (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="px-4 flex-1 overflow-y-auto min-h-0">
            <TransactionTable
              data={transactions}
              onEdit={handleEdit}
              onDelete={handleDelete}
              enableInfiniteScroll
              onLoadMore={handleLoadMore}
              loadingMore={isFetchingNextPage}
              hasMore={hasNextPage}
              isLoading={isLoading}
            />
          </div>
          {/* H5 Modal: Add Trade button fixed at bottom */}
          {currentType !== PortfolioType.EXTERNAL && (
            <div className="px-4 py-3 shrink-0 border-border_solid relative">
              <button
                aria-label="Transactions Details"
                data-openreplay-label="Transactions Details Button"
                id="btn-transactions-details"
                type="button"
                onClick={handleAddTrade}
                className="w-full flex min-h-[44px] items-center justify-center gap-1 rounded-lg border border-border_white_transparent20 px-3 py-2 button_m text-content_primary transition hover:bg-background_white_overlay10 focus:outline-none focus-visible:ring-2 focus-visible:ring-background_white_overlay40"
              >
                <Icon
                  name="addtransfer"
                  size={20}
                  className="text-content_primary"
                />
                {I18n.t("Add Trade")}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div
          className={cn(
            "mx-auto flex w-full flex-col",
            isModal
              ? "px-5 pt-6 flex-1 overflow-y-auto gap-6"
              : "px-5 pt-[52px] md:pt-6 md:px-[60px] gap-6",
          )}
        >
          {/* PC/Page Header */}
          <div className="flex gap-4 flex-row items-center justify-between">
            <div>
              <h1
                aria-label="Transactions Details h1"
                data-openreplay-label="Transactions Details h1 Semantic"
                id="sem-transactions-details-h1-2"
                className="headline_regular_s text-content_primary"
              >
                {symbol} {I18n.t("Transactions Details")}
              </h1>
            </div>
            {currentType !== PortfolioType.EXTERNAL && (
              <button
                aria-label="Transactions Details"
                data-openreplay-label="Transactions Details Button"
                id="btn-transactions-details-2"
                type="button"
                onClick={handleAddTrade}
                className="flex min-h-[36px] min-w-[120px] items-center justify-center gap-1 rounded-lg border border-border_white_transparent20 px-3 py-2 button_m text-content_primary transition hover:bg-background_white_overlay10 focus:outline-none focus-visible:ring-2 focus-visible:ring-background_white_overlay40"
              >
                <Icon
                  name="addtransfer"
                  size={20}
                  className="text-content_primary"
                />
                {I18n.t("Add Trade")}
              </button>
            )}
          </div>

          <TransactionTable
            data={transactions}
            onEdit={handleEdit}
            onDelete={handleDelete}
            enableInfiniteScroll
            onLoadMore={handleLoadMore}
            loadingMore={isFetchingNextPage}
            hasMore={hasNextPage}
            isLoading={isLoading}
            currency={currency}
          />
        </div>
      )}
    </div>
  );
};

/**
 * TransactionsDetails - Page component (uses route params)
 */
export const TransactionsDetails: React.FC = () => {
  const { portfolioId: id, assetId } = useParams<{
    portfolioId: string;
    assetId: string;
  }>();
  const searchParams = useSearchParams();

  return (
    <TransactionsDetailsContent
      portfolioId={id || ""}
      assetId={assetId || ""}
      assetType={searchParams.get("assetType") as PositionAssetTypeEnum}
      symbol={searchParams.get("symbol") || undefined}
      currency={searchParams.get("currency") || "USD"}
    />
  );
};

/**
 * TransactionsDetailsModalCreateAble - Modal version for H5
 */
export const TransactionsDetailsModalCreateAble = async (
  props: TransactionsDetailsContentProps,
): Promise<void> => {
  return new Promise((resolve) => {
    EdgenModalCreateAble.call({
      children: (
        <TransactionsDetailsContent
          {...props}
          isModal={true}
          onClose={() => {
            EdgenModalCreateAble.end(false);
            resolve();
          }}
        />
      ),
      containerClassName: "max-w-full w-full h-full md:w-[800px] md:h-auto",
      contentStyle: {
        borderRadius: 16,
        backgroundColor: ospThemes.colors.background_solid_secondary,
        width: "100%",
        height: "calc(100vh - 54px)",
        padding: 0,
      },
      containerStyle: {
        maxHeight: "calc(100vh - 54px)",
      },
      titleStyle: {
        backgroundColor: ospThemes.colors.background_solid_secondary,
      },
      destroyOnClose: true,
      onCancel: () => {
        EdgenModalCreateAble.end(false);
        resolve();
      },
    });
  });
};
